package com.arena_web.ui.sections.profile

import androidx.compose.runtime.*
import com.arena_web.core.viewmodel.CardShopUiState
import com.arena_web.core.viewmodel.CardShopViewModel
import com.arena_web.core.viewmodel.rememberViewModel
import com.arena_web.data.dto.*
import com.arena_web.domain.repositories.CardRepository
import com.arena_web.di.LocalAppContainer
import com.arena_web.ui.components.cards.PlayerCard
import com.arena_web.ui.components.cards.PlayerCardSize
import com.arena_web.ui.components.feed.CreatePostPopup
import com.arena_web.ui.components.feed.CreateStoryPopup
import com.arena_web.ui.components.feed.PostImageCollage
import com.arena_web.ui.components.icons.Icon
import com.arena_web.ui.components.icons.TintedGlyph
import com.arena_web.ui.components.navigation.TimeSwitcher
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.components.widgets.GradientBorderBox
import com.arena_web.ui.components.widgets.HiddenScrollbarStyle
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import com.arena_web.ui.theme.*
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img

enum class PlayerHubTab(val icon: String) {
    Content(AppIcons.Newspaper),
    Stats(AppIcons.Statistics),
    CardShop(AppIcons.Cards),
}

// Per Figma "Border/gradient/01" — Linear Gradient with 30% layer opacity
// over #3C79F173 (= rgba(60,121,241,0.45)) → #05040C1A (= rgba(5,4,12,0.10)).
// Final rendered alphas after the 30% layer multiplier: 0.135 and 0.03.
private const val OUTER_BORDER_GRADIENT =
    "linear-gradient(204deg, rgba(60, 121, 241, 0.135), rgba(5, 4, 12, 0.03))"
private const val OUTER_BG =
    "linear-gradient(204deg, rgba(60, 121, 241, 0.135) 0%, rgba(5, 4, 12, 0.03) 61.76%)"
private const val INNER_BORDER = "1px solid rgba(41, 52, 138, 0.2)"
// Per Figma "Border/inner/L" — Linear Gradient with 70% layer opacity
// over #29348A33 (= rgba(41,52,138,0.20)) → #0357E633 (= rgba(3,87,230,0.20)).
// Final rendered alphas after the 70% layer multiplier: 0.14 / 0.14.
private const val INNER_BORDER_GRADIENT =
    "linear-gradient(-84deg, rgba(41, 52, 138, 0.14), rgba(3, 87, 230, 0.14))"
// Per Figma Block_Goals (29.68deg, 0.03 alpha) layered over Block_Assists_Goals
// (172.59deg, 0.135 alpha) — gives Card Shop a slightly more saturated bluish
// tint than the bare Player Hub bg.
private const val INNER_BG =
    "linear-gradient(29.68deg, rgba(0, 59, 174, 0.03) 26.9%, rgba(3, 87, 230, 0.03) 67.5%), " +
    "linear-gradient(172.59deg, rgba(60, 121, 241, 0.135) 13.94%, rgba(5, 4, 12, 0.03) 69.49%)"
private const val PILL_BORDER = "0.3px solid rgba(255, 255, 255, 0.4)"
private const val SOLID_DARK_PILL = "rgba(11, 22, 46, 0.8)"

@Composable
fun PlayerHubSection(
    sportName: String,
    sportId: String,
    isOwnProfile: Boolean = true,
    cardRepository: CardRepository? = null,
    loadPosts: (suspend () -> List<UserPostDto>)? = null,
    canCreatePost: Boolean = false,
    activeCardId: String? = null,
    onActiveCardChanged: (UserCardDto) -> Unit = {},
    statsCharts: Map<String, StatsChartDto> = emptyMap(),
    goalsChartPeriod: String = "year",
    assistsChartPeriod: String = "month",
    onGoalsPeriodChanged: (String) -> Unit = {},
    onAssistsPeriodChanged: (String) -> Unit = {},
    userName: String = "",
    userAvatarUrl: String? = null,
    sportProfile: SportProfileDto? = null,
    // Owner of the profile being rendered. Required when viewing someone
    // else's public profile so the Content tab can fetch THEIR stories
    // instead of the viewer's own.
    profileUserId: String? = null,
    modifier: Modifier = Modifier
) {
    val t = LocalAppStrings.current
    var activeTab by remember { mutableStateOf(PlayerHubTab.Content) }

    // Card-shop view-model and purchase-modal state are hoisted to this level
    // (above the backdrop-filter container) so the purchase popup, which uses
    // position: fixed, is anchored to the viewport rather than to the inner
    // Player Hub block.
    // Re-key on profileUserId too so navigating between profiles loads the
    // right collection; without it the cached state from the previous user
    // would briefly leak into the current one.
    val cardShopViewModel = if (cardRepository != null) {
        rememberViewModel(key = "$sportId|${if (isOwnProfile) "me" else (profileUserId ?: "")}") {
            CardShopViewModel(
                cardRepository,
                profileUserId = if (isOwnProfile) null else profileUserId,
            )
        }
    } else null
    val cardShopState = cardShopViewModel?.uiState?.collectAsState()?.value
    var pendingPurchase by remember { mutableStateOf<UserCardDto?>(null) }
    // Frozen (subscription-locked) card tapped → open the Access paywall so the
    // user can renew to thaw it. Rendered as a sibling of the Column below.
    var showPaywall by remember { mutableStateOf(false) }

    // Story/Post creation popups — hoisted for the same reason: backdrop-filter
    // on the Player Hub Column would clip a position:fixed descendant.
    val appContainer = LocalAppContainer.current
    val storyLikeScope = rememberCoroutineScope()
    val postRepository = appContainer.userPostRepository
    val storyRepository = appContainer.userStoryRepository
    var showCreateStory by remember { mutableStateOf(false) }
    var showCreatePost by remember { mutableStateOf(false) }
    var postsReloadTrigger by remember { mutableStateOf(0) }
    var storiesReloadTrigger by remember { mutableStateOf(0) }
    // Comments modal target — hoisted for the same backdrop-filter reason as
    // the create-post popup; rendered as a sibling of the Player Hub Column.
    var commentsTarget by remember { mutableStateOf<UserPostDto?>(null) }
    // Story-viewer popup (Figma 8672:444857) — hoisted for the same reason.
    // `storyViewerIndex` doubles as "open" flag: non-null = modal is visible.
    var storyViewerStories by remember { mutableStateOf<List<UserStoryDto>>(emptyList()) }
    var storyViewerIndex by remember { mutableStateOf<Int?>(null) }

    // Posts + stories live here (not inside `ContentTab`) so switching
    // away to another tab and back doesn't dispose the state and re-
    // trigger the loadPosts / getStories network calls each time. The
    // existing reload triggers (`postsReloadTrigger`, `storiesReloadTrigger`)
    // and explicit profile changes (sport/user/repo identity) still
    // force a refetch.
    var fetchedPosts by remember { mutableStateOf<List<UserPostDto>>(emptyList()) }
    var isLoadingPosts by remember { mutableStateOf(false) }
    var stories by remember { mutableStateOf<List<UserStoryDto>>(emptyList()) }

    LaunchedEffect(cardShopState?.myCards) {
        val pending = pendingPurchase ?: return@LaunchedEffect
        if (cardShopState?.myCards?.any { it.templateId == pending.templateId } == true) {
            pendingPurchase = null
        }
    }
    // Subscribed → the shop is included in the plan: claim every template
    // silently so it lands in Active & Bought, and drop the Available-for-
    // purchase section. It only re-appears as a manual fallback when a claim
    // request failed.
    val isSubscribed by appContainer.isSubscribed.collectAsState()
    LaunchedEffect(isOwnProfile) {
        if (isOwnProfile) appContainer.ensureSubscriptionLoaded()
    }
    LaunchedEffect(isSubscribed, cardShopState?.shopCards, cardShopState?.autoClaimFailed) {
        if (isOwnProfile && isSubscribed &&
            cardShopState?.shopCards?.isNotEmpty() == true &&
            cardShopState?.autoClaimFailed != true
        ) {
            cardShopViewModel?.autoClaimShop()
        }
    }
    val hideShopSection = isSubscribed && cardShopState?.autoClaimFailed != true
    LaunchedEffect(loadPosts, postsReloadTrigger) {
        if (loadPosts == null) return@LaunchedEffect
        isLoadingPosts = true
        try {
            fetchedPosts = loadPosts()
        } catch (_: Throwable) { /* keep empty */ }
        isLoadingPosts = false
    }
    LaunchedEffect(storyRepository, storiesReloadTrigger, isOwnProfile, profileUserId) {
        val repo = storyRepository ?: return@LaunchedEffect
        val result = when {
            isOwnProfile -> repo.getMyStories()
            profileUserId != null -> repo.getStoriesByUser(profileUserId)
            else -> {
                stories = emptyList()
                return@LaunchedEffect
            }
        }
        result.onSuccess { items ->
            stories = items.sortedBy { it.createdAt }
        }
    }

    Column(
        modifier = modifier
            .fillMaxHeight()
            .sBorderRadius(40)
            .overflow(Overflow.Hidden)
            .styleModifier {
                property("backdrop-filter", "blur(${s(11.165)})")
                property("-webkit-backdrop-filter", "blur(${s(11.165)})")
                property("background", OUTER_BG)
                property("position", "relative")
            }
    ) {
        // Gradient border ring — mask-composite trick so the gradient follows the radius-40 corners.
        Box(
            modifier = Modifier
                .fillMaxSize()
                .styleModifier {
                    property("position", "absolute")
                    property("inset", "0")
                    property("border", "1px solid transparent")
                    property("border-radius", s(40))
                    property("background", "$OUTER_BORDER_GRADIENT border-box")
                    property("-webkit-mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                    property("mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                    property("-webkit-mask-composite", "xor")
                    property("mask-composite", "exclude")
                    property("pointer-events", "none")
                }
        )
        Column(
            modifier = Modifier
                .fillMaxSize()
                .styleModifier {
                    property("padding", "${s(16)} ${s(18)} ${s(16)} ${s(22)}")
                }
                .sGap(24)
        ) {
            // Section Header Container: title row + segmented control (gap-12)
            Column(modifier = Modifier.fillMaxWidth().sGap(12)) {
                // Header: title + settings icon (48x48 container)
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    SpanText(
                        t.profileHubTitle,
                        modifier = Modifier
                            .sFontSize(24)
                            .fontWeight(AppTypography.Medium)
                            .sLineHeight(32)
                            .sLetterSpacing(0.24)
                            .color(com.arena_web.ui.theme.ThemeVars.Text2)
                            .flexGrow(1)
                    )
                    Box(
                        modifier = Modifier.sSize(48).cursor(Cursor.Pointer),
                        contentAlignment = Alignment.Center
                    ) {
                        Icon(
                            src = AppIcons.PlayerHub,
                            modifier = Modifier.sWidth(21).sHeight(21)
                        )
                    }
                }

                // Segmented tab control (centered)
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = com.varabyte.kobweb.compose.foundation.layout.Arrangement.Center
                ) {
                    TabSegmentedControl(active = activeTab, onSelect = { activeTab = it })
                }
            }

            // Inner content card. Stats tab has its own per-chart cards (per Figma
            // 7861:355893), so we skip the wrapper background/border for that tab to
            // avoid an extra visual layer.
            val showInnerCardChrome = activeTab != PlayerHubTab.Stats
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .flexGrow(1)
                    .minHeight(0.px)
                    .sBorderRadius(40)
                    .overflow(Overflow.Hidden)
                    .styleModifier {
                        if (showInnerCardChrome) {
                            property("background", INNER_BG)
                        }
                        property("position", "relative")
                    }
            ) {
                // Gradient border ring (Border/inner/L) — mask-composite trick to follow radius-40.
                if (showInnerCardChrome) {
                    Box(
                        modifier = Modifier
                            .fillMaxSize()
                            .styleModifier {
                                property("position", "absolute")
                                property("inset", "0")
                                property("border", "1px solid transparent")
                                property("border-radius", s(40))
                                property("background", "$INNER_BORDER_GRADIENT border-box")
                                property("-webkit-mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                                property("mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                                property("-webkit-mask-composite", "xor")
                                property("mask-composite", "exclude")
                                property("pointer-events", "none")
                            }
                    )
                }
                when (activeTab) {
                    PlayerHubTab.CardShop -> CardShopTab(
                        viewModel = cardShopViewModel,
                        state = cardShopState,
                        sportName = sportName,
                        activeCardId = activeCardId,
                        onActiveCardChanged = onActiveCardChanged,
                        isOwnProfile = isOwnProfile,
                        hideShopSection = hideShopSection,
                        onCardSelected = { card ->
                            if (isOwnProfile && cardShopState?.purchasing != true) {
                                cardShopViewModel?.clearPurchaseError()
                                pendingPurchase = card
                            }
                        },
                        onFrozenCardClick = { showPaywall = true }
                    )
                    PlayerHubTab.Stats -> StatsTab(
                        sportName = sportName,
                        statsCharts = statsCharts,
                        goalsChartPeriod = goalsChartPeriod,
                        assistsChartPeriod = assistsChartPeriod,
                        onGoalsPeriodChanged = onGoalsPeriodChanged,
                        onAssistsPeriodChanged = onAssistsPeriodChanged
                    )
                    PlayerHubTab.Content -> ContentTab(
                        canCreatePost = canCreatePost,
                        userName = userName,
                        userAvatarUrl = userAvatarUrl,
                        activeCard = cardShopState?.myCards?.firstOrNull { it.id == activeCardId },
                        sportName = sportName,
                        sportProfile = sportProfile,
                        isOwnProfile = isOwnProfile,
                        profileUserId = profileUserId,
                        fetchedPosts = fetchedPosts,
                        isLoadingPosts = isLoadingPosts,
                        stories = stories,
                        onAddStoryClick = { if (storyRepository != null) showCreateStory = true },
                        onAddPostClick = { if (postRepository != null) showCreatePost = true },
                        onOpenComments = { post -> commentsTarget = post },
                        onOpenStory = { allStories, idx ->
                            storyViewerStories = allStories
                            storyViewerIndex = idx
                        },
                        onPostsUpdated = { fetchedPosts = it },
                    )
                }
            }
        }
    }

    // Rendered as a sibling of the Player Hub Column so its position: fixed
    // is anchored to the viewport (the Player Hub Column has backdrop-filter,
    // which would otherwise create a containing block for fixed descendants).
    // Access paywall — opened when a frozen card is tapped. Sibling of the
    // Column so its position:fixed overlay isn't clipped by the backdrop-filter.
    com.arena_web.ui.components.overlay.SubscribeModal(
        isOpen = showPaywall,
        onClose = { showPaywall = false },
        source = "card_frozen",
    )

    com.arena_web.ui.components.overlay.PurchaseCardModal(
        card = pendingPurchase,
        sportName = sportName,
        purchasing = cardShopState?.purchasing == true,
        purchaseError = cardShopState?.purchaseError,
        onConfirm = { card -> cardShopViewModel?.purchaseCard(card.templateId) },
        onDismiss = {
            if (cardShopState?.purchasing != true) {
                cardShopViewModel?.clearPurchaseError()
                pendingPurchase = null
            }
        }
    )

    // Story/Post creation popups — siblings of the Column for the same reason.
    if (storyRepository != null) {
        CreateStoryPopup(
            isOpen = showCreateStory,
            storyRepository = storyRepository,
            onDismiss = { showCreateStory = false },
            onCreated = {
                showCreateStory = false
                storiesReloadTrigger++
            }
        )
    }
    if (postRepository != null) {
        CreatePostPopup(
            isOpen = showCreatePost,
            postRepository = postRepository,
            onDismiss = { showCreatePost = false },
            onCreated = {
                showCreatePost = false
                postsReloadTrigger++
            }
        )
    }

    // Story-viewer popup — Figma 8672:444857. Same sibling-of-Column placement
    // so its fixed overlay isn't clipped by the Player Hub's backdrop-filter.
    val viewerIdx = storyViewerIndex
    if (viewerIdx != null && storyViewerStories.isNotEmpty()) {
        com.arena_web.ui.components.overlay.StoryViewerModal(
            stories = storyViewerStories,
            initialIndex = viewerIdx,
            title = if (isOwnProfile) t.profileHubYourStories else userName.ifBlank { t.profileHubStories },
            sportName = sportName,
            activeCard = cardShopState?.myCards?.firstOrNull { it.id == activeCardId },
            avatarUrl = userAvatarUrl,
            onDismiss = { storyViewerIndex = null },
            onLikeClick = storyRepository?.let { repo ->
                { storyId ->
                    // Optimistic toggle on the viewer's list (and the row's), then
                    // reconcile with the server; revert both on failure.
                    val before = storyViewerStories
                    val beforeRow = stories
                    val toggle: (UserStoryDto) -> UserStoryDto = { st ->
                        if (st.id != storyId) st
                        else st.copy(
                            isLikedByMe = !st.isLikedByMe,
                            likesCount = (st.likesCount + if (st.isLikedByMe) -1 else 1)
                                .coerceAtLeast(0),
                        )
                    }
                    storyViewerStories = storyViewerStories.map(toggle)
                    stories = stories.map(toggle)
                    storyLikeScope.launch {
                        repo.likeStory(storyId).onFailure {
                            storyViewerStories = before
                            stories = beforeRow
                        }
                    }
                }
            },
            // Own profile only: "⋮" in the identity row → Delete.
            onDeleteClick = storyRepository?.takeIf { isOwnProfile }?.let { repo ->
                { storyId ->
                    storyViewerIndex = null
                    storyViewerStories = storyViewerStories.filter { it.id != storyId }
                    stories = stories.filter { it.id != storyId }
                    storyLikeScope.launch {
                        repo.deleteStory(storyId).onFailure { storiesReloadTrigger++ }
                    }
                }
            },
        )
    }

    // Comments popup — Figma 8539:404523. Rendered as a sibling of the Player
    // Hub Column so its `position: fixed` overlay is anchored to the viewport.
    com.arena_web.ui.components.overlay.PostCommentsModal(
        isOpen = commentsTarget != null,
        post = commentsTarget,
        repository = postRepository,
        onDismiss = { commentsTarget = null },
        onCommentsChanged = { newCount ->
            // Reflect the new comments count in the underlying post list so the
            // chat-icon badge stays in sync when the modal closes.
            commentsTarget?.let { current ->
                commentsTarget = current.copy(commentsCount = newCount)
            }
            postsReloadTrigger++
        }
    )
}

@Composable
private fun TabSegmentedControl(active: PlayerHubTab, onSelect: (PlayerHubTab) -> Unit) {
    val sportColors = LocalSportColors.current
    GradientBorderBox(
        modifier = Modifier.sHeight(48),
        borderRadius = 34,
        background = AppGradients.InteractiveSecondary,
        borderWidth = "0.3px"
    ) {
        Row(
            modifier = Modifier.sPadding(2).sGap(4),
            verticalAlignment = Alignment.CenterVertically
        ) {
            PlayerHubTab.entries.forEach { tab ->
                val isActive = tab == active
                Box(
                    modifier = Modifier
                        .sSize(44)
                        .sBorderRadius(100)
                        .cursor(Cursor.Pointer)
                        .onClick { onSelect(tab) }
                        .then(
                            if (isActive) Modifier.backgroundColor(sportColors.primary)
                            else Modifier
                        ),
                    contentAlignment = Alignment.Center
                ) {
                    Icon(
                        src = tab.icon,
                        modifier = Modifier.sSize(24).styleModifier {
                            property(
                                "filter",
                                if (isActive) sportColors.iconFilterOnPrimary else com.arena_web.ui.theme.ThemeVars.ICON_FILTER_V
                            )
                        }
                    )
                }
            }
        }
    }
}

// =============================================================================
// Card Shop tab
// =============================================================================
@Composable
private fun CardShopTab(
    viewModel: CardShopViewModel?,
    state: CardShopUiState?,
    sportName: String,
    activeCardId: String?,
    onActiveCardChanged: (UserCardDto) -> Unit,
    isOwnProfile: Boolean,
    onCardSelected: (UserCardDto) -> Unit,
    onFrozenCardClick: () -> Unit = {},
    /** Subscribed viewer — the shop is auto-claimed, so the section is dropped. */
    hideShopSection: Boolean = false,
) {
    val t = LocalAppStrings.current
    if (viewModel == null || state == null) {
        EmptyTabState(t.profileHubCardShopUnavailable)
        return
    }

    Column(
        modifier = Modifier
            .fillMaxSize()
            .styleModifier {
                // No horizontal padding on the outer container so the horizontal
                // card lists span the full width of the inner card; first/last
                // cards get their breathing room from the row's own padding-inline.
                property("padding", "${s(16)} 0 ${s(18)} 0")
            }
            .sGap(22)
    ) {
        // Header: title + cart icon on first row, subtitle below (px-24, gap-6)
        Column(
            modifier = Modifier.fillMaxWidth().sPadding(0, 24).sGap(6)
        ) {
            Row(
                modifier = Modifier.fillMaxWidth().sPaddingTop(8),
                verticalAlignment = Alignment.CenterVertically
            ) {
                SpanText(
                    t.profileHubCardShopTitle,
                    modifier = Modifier
                        .sFontSize(22)
                        .fontWeight(AppTypography.Medium)
                        .sLineHeight(26)
                        .sLetterSpacing(0.22)
                        .color(com.arena_web.ui.theme.ThemeVars.Text2)
                        .flexGrow(1)
                )
                Box(
                    modifier = Modifier.sWidth(27).sHeight(24),
                    contentAlignment = Alignment.Center
                ) {
                    Icon(
                        src = AppIcons.Cart,
                        modifier = Modifier.sWidth(27).sHeight(24)
                    )
                }
            }
            SpanText(
                t.profileHubCardShopSubtitle,
                modifier = Modifier
                    .sFontSize(16)
                    .fontWeight(AppTypography.Regular)
                    .sLineHeight(24)
                    .sLetterSpacing(0.16)
                    .color(com.arena_web.ui.theme.ThemeVars.TextMuted)
            )
        }

        Column(
            modifier = HiddenScrollbarStyle.toModifier()
                .fillMaxWidth()
                .flexGrow(1)
                .minHeight(0.px)
                .styleModifier {
                    property("overflow-y", "auto")
                    property("scroll-behavior", "smooth")
                }
                .sGap(28)
        ) {
            // Owned cards as a vertical wrapping grid on BOTH own and public
            // profiles (mobile parity): the "Active & Bought / N owned" header
            // is gone and the row no longer scrolls horizontally.
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .sPaddingBottom(28)
                    .sGap(24)
            ) {
                OwnedCardsGrid(
                    cards = state.myCards,
                    sportName = sportName,
                    onFrozenClick = if (isOwnProfile) onFrozenCardClick else null,
                )
            }

            // The "Available for purchase" shop section was removed from the
            // desktop profile (user decision 2026-08) — unowned templates are
            // gated via the card-template picker / paywall instead. The mobile
            // profile keeps its own shop section (mobile-app parity).
        }
    }
}

@Composable
private fun CardGridRow(
    empty: Boolean,
    emptyText: String,
    content: @Composable () -> Unit
) {
    if (empty) {
        Box(
            modifier = Modifier.fillMaxWidth().sHeight(170),
            contentAlignment = Alignment.Center
        ) {
            SpanText(
                emptyText,
                modifier = Modifier.bodySRegular().color(com.arena_web.ui.theme.ThemeVars.Text3)
            )
        }
        return
    }
    val rowId = remember { "card-grid-${kotlin.random.Random.nextInt(1_000_000)}" }
    DisposableEffect(rowId) {
        val el = kotlinx.browser.document.getElementById(rowId)
        if (el == null) {
            onDispose { }
        } else {
            // Make sure the leading padding is visible on first paint.
            el.asDynamic().scrollLeft = 0
            // Convert vertical wheel into horizontal scroll on this row
            val whHandler: (org.w3c.dom.events.Event) -> Unit = { e ->
                val dx = (e.asDynamic().deltaX as Number).toDouble()
                val dy = (e.asDynamic().deltaY as Number).toDouble()
                val delta = if (dx != 0.0) dx else dy
                if (delta != 0.0) {
                    e.preventDefault()
                    el.asDynamic().scrollBy(kotlin.js.json("left" to delta, "behavior" to "smooth"))
                }
            }
            // Wheel must be non-passive so preventDefault() can stop the page from scrolling
            el.asDynamic().addEventListener(
                "wheel",
                whHandler,
                kotlin.js.json("passive" to false)
            )
            onDispose {
                el.removeEventListener("wheel", whHandler)
            }
        }
    }
    Div(attrs = HiddenScrollbarStyle.toModifier()
        .id(rowId)
        .fillMaxWidth()
        .styleModifier {
            property("display", "flex")
            property("flex-direction", "row")
            property("flex-wrap", "nowrap")
            property("gap", s(22))
            property("overflow-x", "auto")
            property("overflow-y", "visible")
            // Inner gutters so the first / last card don't sit flush against the row edges.
            property("padding-left", s(24))
            property("padding-right", s(24))
            property("padding-bottom", s(4))
            property("scroll-behavior", "smooth")
        }
        .toAttrs()
    ) {
        content()
    }
}

/**
 * 2-row wrapping grid for owned cards on a public profile. Uses a flex
 * `wrap` container so the second half overflows onto a second row; if the
 * viewer has more cards than fit in two rows the extras wrap to a third —
 * which is intentional, we'd rather show everything than truncate.
 */
@Composable
private fun OwnedCardsGrid(
    cards: List<UserCardDto>,
    sportName: String,
    /** Own profile: tapping a frozen card opens the Access paywall. */
    onFrozenClick: (() -> Unit)? = null,
) {
    val t = LocalAppStrings.current
    if (cards.isEmpty()) {
        Box(
            modifier = Modifier.fillMaxWidth().sHeight(170),
            contentAlignment = Alignment.Center
        ) {
            SpanText(
                t.profileHubNoCardsYet,
                modifier = Modifier.bodySRegular().color(com.arena_web.ui.theme.ThemeVars.Text3)
            )
        }
        return
    }
    Div(attrs = Modifier
        .fillMaxWidth()
        .sPadding(0, 24)
        .styleModifier {
            property("display", "flex")
            property("flex-direction", "row")
            property("flex-wrap", "wrap")
            property("gap", "${s(22)} ${s(22)}")
            property("justify-content", "flex-start")
        }
        .toAttrs()
    ) {
        cards.forEach { card ->
            OwnedCardThumb(card = card, sportName = sportName, onFrozenClick = onFrozenClick)
        }
    }
}

@Composable
private fun ShopSectionHeader(
    title: String,
    meta: String,
    paddingLeft: Number = 0,
    paddingRight: Number = 16,
) {
    Row(
        modifier = Modifier.fillMaxWidth()
            .sPaddingLeft(paddingLeft)
            .sPaddingRight(paddingRight),
        verticalAlignment = Alignment.CenterVertically
    ) {
        SpanText(
            title,
            modifier = Modifier
                .sFontSize(18)
                .fontWeight(AppTypography.Medium)
                .sLineHeight(22)
                .sLetterSpacing(0.09)
                .color(com.arena_web.ui.theme.ThemeVars.Text2)
                .flexGrow(1)
        )
        SpanText(
            meta,
            modifier = Modifier
                .sFontSize(14)
                .fontWeight(AppTypography.Regular)
                .sLineHeight(22)
                .sLetterSpacing(0.028)
                .color(com.arena_web.ui.theme.ThemeVars.TextMuted)
        )
    }
}

@Composable
private fun OwnedCardThumb(
    card: UserCardDto,
    sportName: String,
    // Own profile only: tapping a frozen card opens the Access paywall. Null
    // (public profile) leaves the frozen thumb non-interactive.
    onFrozenClick: (() -> Unit)? = null,
) {
    Box(
        modifier = Modifier
            .styleModifier {
                property("flex-shrink", "0")
                property("scroll-snap-align", "start")
                property("position", "relative")
            }
            .then(
                if (card.frozen && onFrozenClick != null) {
                    Modifier.cursor(Cursor.Pointer).onClick { onFrozenClick() }
                } else Modifier
            ),
        contentAlignment = Alignment.Center
    ) {
        // Frozen cards render their REAL frame + generated avatar (no dimming) —
        // the PRO+ chip alone gates them.
        PlayerCard(
            playerName = "",
            rating = 0,
            position = "",
            sport = sportName,
            size = PlayerCardSize.Medium,
            cardFrameUrl = card.cardImageUrl,
            avatarUrl = card.avatarUrl,
            cardAssetsBasePath = card.assetsBasePath,
            cardTextColor = card.textColor,
            hideRating = true
        )
        if (card.frozen) {
            com.arena_web.ui.components.cards.FrozenCardOverlay(compact = true)
        }
    }
}

@Composable
private fun LockCardThumb(
    card: UserCardDto,
    sportName: String,
    onClick: () -> Unit
) {
    val sportColors = LocalSportColors.current
    Column(
        modifier = Modifier
            .sGap(6)
            .cursor(Cursor.Pointer)
            .onClick { onClick() }
            .styleModifier {
                property("flex-shrink", "0")
                property("scroll-snap-align", "start")
            },
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        Box(
            modifier = Modifier.styleModifier { property("position", "relative") },
            contentAlignment = Alignment.Center
        ) {
            // Card with reduced opacity (locked state)
            Box(
                modifier = Modifier.styleModifier { property("opacity", "0.55") },
                contentAlignment = Alignment.Center
            ) {
                PlayerCard(
                    playerName = "",
                    rating = 0,
                    position = "",
                    sport = sportName,
                    size = PlayerCardSize.Medium,
                    cardFrameUrl = card.cardImageUrl,
                    cardAssetsBasePath = card.assetsBasePath,
                    cardTextColor = card.textColor,
                    hideRating = true
                )
            }
            // Lock badge overlay (centered)
            Box(
                modifier = Modifier
                    .sSize(40)
                    .sBorderRadius(100)
                    .styleModifier {
                        property("position", "absolute")
                        property("background", SOLID_DARK_PILL)
                        property("border", "0.3px solid var(--arena-border-top)")
                        property("backdrop-filter", "blur(4px)")
                    },
                contentAlignment = Alignment.Center
            ) {
                SpanText(
                    "🔒",
                    modifier = Modifier.sFontSize(18)
                )
            }
        }
        // Locked (non-default) cards are gated behind the Access subscription —
        // the lock badge above communicates this; no per-card price is shown.
    }
}

// =============================================================================
// Stats tab — Figma 7861:355893 "Block_Assists_Goals"
// =============================================================================

/** Sport-specific stat keys + display labels for the two profile charts.
 *  Mirrors the Pro Lineups leaderboard mapping (`LeaderStatKeysBySport`):
 *  scoring + playmaking for team sports, games + sets for racket sports.
 *  Falls back to football's goals/assists for unrecognised sports. */
private data class ChartStatPair(
    val primaryKey: String,
    val primaryLabel: String,
    val secondaryKey: String,
    val secondaryLabel: String,
)

private fun statsChartPairForSport(sport: String): ChartStatPair = when (sport.lowercase()) {
    "football"   -> ChartStatPair("goals", "Goals", "assists", "Assists")
    "basketball" -> ChartStatPair("points", "Points", "assists", "Assists")
    "volleyball" -> ChartStatPair("kills", "Kills", "assists", "Assists")
    "tennis"     -> ChartStatPair("games", "Games", "sets_won", "Sets Won")
    "padel"      -> ChartStatPair("games", "Games", "sets_won", "Sets Won")
    else         -> ChartStatPair("goals", "Goals", "assists", "Assists")
}

/** Sports for which there is no defined chart stat schema (Other and
 *  the future "All" cross-sport view). Showing the goals/assists chart
 *  for these would be misleading — there are no canonical stat keys to
 *  aggregate against. */
private fun sportSupportsCharts(sport: String): Boolean = when (sport.lowercase()) {
    "football", "basketball", "tennis", "volleyball", "padel" -> true
    else -> false
}

@Composable
private fun StatsTab(
    sportName: String,
    statsCharts: Map<String, StatsChartDto>,
    goalsChartPeriod: String,
    assistsChartPeriod: String,
    onGoalsPeriodChanged: (String) -> Unit,
    onAssistsPeriodChanged: (String) -> Unit
) {
    val t = LocalAppStrings.current
    // "Other" + the cross-sport "All" view have no comparable stat
    // schema (Other has no defined stat types in `sport_stat_types`; All
    // would mix incompatible metrics — goals vs games vs points). Show
    // an empty-state prompt instead of rendering charts that would
    // either be empty or aggregate apples + oranges.
    if (!sportSupportsCharts(sportName)) {
        StatsChartsEmptyState(modifier = Modifier.fillMaxSize())
        return
    }

    val goalsData = resolveDataPoints(statsCharts[goalsChartPeriod]?.dataPoints, goalsChartPeriod)
    val assistsData = resolveDataPoints(statsCharts[assistsChartPeriod]?.dataPoints, assistsChartPeriod)
    val tooltipIcon = AppIcons.ballSmall(sportName)
    val stats = statsChartPairForSport(sportName)
    val primaryTitle = t.profileHubChartTitle(stats.primaryLabel, chartPeriodLabel(goalsChartPeriod))
    val secondaryTitle = t.profileHubChartTitle(stats.secondaryLabel, chartPeriodLabel(assistsChartPeriod))

    Column(
        modifier = Modifier
            .fillMaxSize()
            .sGap(20)
    ) {
        StatsChartCard(
            title = primaryTitle,
            chartPeriod = goalsChartPeriod,
            onPeriodChanged = onGoalsPeriodChanged,
            modifier = Modifier.flexGrow(1).minHeight(0.px)
        ) {
            BarChart(
                dataPoints = goalsData,
                statKey = stats.primaryKey,
                period = goalsChartPeriod,
                tooltipIcon = tooltipIcon
            )
        }
        StatsChartCard(
            title = secondaryTitle,
            chartPeriod = assistsChartPeriod,
            onPeriodChanged = onAssistsPeriodChanged,
            modifier = Modifier.flexGrow(1).minHeight(0.px)
        ) {
            LineChart(
                dataPoints = assistsData,
                statKey = stats.secondaryKey,
                period = assistsChartPeriod,
                tooltipIcon = tooltipIcon
            )
        }
    }
}

private fun chartPeriodLabel(period: String): String = when (period.lowercase()) {
    "week" -> "week"
    "month" -> "month"
    "year" -> "year"
    else -> period
}

/** Centred empty state for the Stats tab when the active sport doesn't
 *  have a comparable chart schema (Other, future All). Uses the project's
 *  bar-chart glyph + title/subtitle to explain why charts are missing
 *  and what the user should do next. */
@Composable
private fun StatsChartsEmptyState(modifier: Modifier = Modifier) {
    val t = LocalAppStrings.current
    Column(
        modifier = modifier,
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.Center,
    ) {
        Column(
            modifier = Modifier.sGap(20),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            // Recolour the bar-chart glyph via CSS mask so the icon
            // matches the project's blue accent (#3C79F1) used in the
            // other empty-state illustrations (`ic_empty_top_strikers`,
            // `ic_empty_matches`, etc.). Same mask trick as the star
            // recolour in `RatePlayerPopup`.
            Box(
                modifier = Modifier
                    .sSize(96)
                    .styleModifier {
                        property("background-color", "#1A2E5E")
                        property("mask-image", "url('${AppIcons.BarChart}')")
                        property("-webkit-mask-image", "url('${AppIcons.BarChart}')")
                        property("mask-size", "contain")
                        property("-webkit-mask-size", "contain")
                        property("mask-position", "center")
                        property("-webkit-mask-position", "center")
                        property("mask-repeat", "no-repeat")
                        property("-webkit-mask-repeat", "no-repeat")
                    }
            )
            Column(
                modifier = Modifier.sGap(8),
                horizontalAlignment = Alignment.CenterHorizontally,
            ) {
                SpanText(
                    t.profileHubNoChartsTitle,
                    modifier = Modifier
                        .sFontSize(22)
                        .fontWeight(FontWeight.SemiBold)
                        .sLineHeight(28)
                        .color(com.arena_web.ui.theme.ThemeVars.Text1)
                        .styleModifier {
                            property("text-align", "center")
                            property("letter-spacing", s(0.11))
                            property("font-family", "Urbanist, sans-serif")
                        }
                )
                SpanText(
                    t.profileHubNoChartsBody,
                    modifier = Modifier
                        .sFontSize(16)
                        .fontWeight(FontWeight.Normal)
                        .sLineHeight(22)
                        .color(com.arena_web.ui.theme.ThemeVars.Text2)
                        .styleModifier {
                            property("text-align", "center")
                            property("max-width", s(380))
                            property("letter-spacing", s(0.08))
                            property("font-family", "Urbanist, sans-serif")
                        }
                )
            }
        }
    }
}

// Figma "Surface/inner/gradient_L" — Linear Gradient · 70% layer opacity over
// #29348A33 (= rgba(41,52,138,0.20)) → #0357E633 (= rgba(3,87,230,0.20)).
// Final alphas after the 70% multiplier: 0.14 / 0.14.
private const val CHART_BLOCK_GRADIENT =
    "linear-gradient(-84deg, rgba(41, 52, 138, 0.14), rgba(3, 87, 230, 0.14))"

@Composable
private fun StatsChartCard(
    title: String,
    chartPeriod: String,
    onPeriodChanged: (String) -> Unit,
    modifier: Modifier = Modifier,
    chart: @Composable () -> Unit
) {
    // Figma 7861:355905/355941 — rounded-40, backdrop-blur, single
    // Surface/inner/gradient_L background (identical on both chart cards).
    Column(
        modifier = modifier
            .fillMaxWidth()
            .sBorderRadius(40)
            .overflow(Overflow.Hidden)
            .styleModifier {
                property("backdrop-filter", "blur(${s(11.165)})")
                property("-webkit-backdrop-filter", "blur(${s(11.165)})")
                property("background", CHART_BLOCK_GRADIENT)
                property("border", INNER_BORDER)
                property("padding", "${s(16)} ${s(16)} ${s(18)} ${s(16)}")
            }
            .sGap(8)
    ) {
        // Header: title H4 20/26 tracking-0.2 + TimeSwitcher, px-12
        Row(
            modifier = Modifier.fillMaxWidth().sPadding(0, 12),
            verticalAlignment = Alignment.CenterVertically
        ) {
            SpanText(
                title,
                modifier = Modifier
                    .sFontSize(20)
                    .fontWeight(AppTypography.Medium)
                    .sLineHeight(26)
                    .sLetterSpacing(0.2)
                    .color(com.arena_web.ui.theme.ThemeVars.Text2)
                    .flexGrow(1)
            )
            TimeSwitcher(chartPeriod, onPeriodChanged)
        }
        // Chart area
        Box(
            modifier = Modifier.fillMaxWidth().flexGrow(1).minHeight(0.px)
        ) {
            chart()
        }
    }
}

// =============================================================================
// Content tab — Stories placeholder + posts grid/list
// =============================================================================
@Composable
private fun ContentTab(
    canCreatePost: Boolean,
    userName: String,
    userAvatarUrl: String?,
    activeCard: UserCardDto?,
    sportName: String,
    sportProfile: SportProfileDto?,
    isOwnProfile: Boolean,
    profileUserId: String?,
    // Posts/stories are loaded once at the `PlayerHubSection` level and
    // passed through — keeps the cache alive across tab switches so the
    // user doesn't pay a network round-trip for every tab toggle.
    fetchedPosts: List<UserPostDto>,
    isLoadingPosts: Boolean,
    stories: List<UserStoryDto>,
    onAddStoryClick: () -> Unit,
    onAddPostClick: () -> Unit,
    onOpenComments: (UserPostDto) -> Unit = {},
    onOpenStory: (List<UserStoryDto>, Int) -> Unit = { _, _ -> },
    // Optimistic like-toggle (and any future post mutations) calls back
    // up so the parent's cached list stays in sync with the UI.
    onPostsUpdated: (List<UserPostDto>) -> Unit = {},
) {
    val t = LocalAppStrings.current
    val container = LocalAppContainer.current
    val feedRepository = container.feedRepository
    val scope = rememberCoroutineScope()

    var viewMode by remember { mutableStateOf(ContentViewMode.Grid) }
    val sportColors = LocalSportColors.current
    val effectivePosts = fetchedPosts

    // Optimistic like toggle — flip the local UI immediately, then hit the API.
    // On failure we revert. `type=USER` because these are user posts.
    val onLikeClick: (UserPostDto) -> Unit = onLike@{ post ->
        val repo = feedRepository ?: return@onLike
        val wasLiked = post.isLikedByMe
        val optimistic = post.copy(
            isLikedByMe = !wasLiked,
            likesCount = (post.likesCount + if (wasLiked) -1 else 1).coerceAtLeast(0)
        )
        onPostsUpdated(fetchedPosts.map { if (it.id == post.id) optimistic else it })
        scope.launch {
            repo.likePost(post.id, "USER").onFailure {
                onPostsUpdated(fetchedPosts.map { if (it.id == post.id) post else it })
            }
        }
    }

    Column(
        modifier = HiddenScrollbarStyle.toModifier()
            .fillMaxSize()
            .styleModifier {
                property("padding", "${s(16)} 0 ${s(18)} 0")
                property("overflow-y", "auto")
            }
            .sGap(20)
    ) {
        // Header: "Content" + tab icon on right (matching Card Shop pattern; px-24 + pt-8)
        Row(
            modifier = Modifier.fillMaxWidth().sPadding(0, 24).sPaddingTop(8),
            verticalAlignment = Alignment.CenterVertically
        ) {
            SpanText(
                t.profileHubContentTitle,
                modifier = Modifier
                    .sFontSize(22)
                    .fontWeight(AppTypography.Medium)
                    .sLineHeight(26)
                    .sLetterSpacing(0.22)
                    .color(com.arena_web.ui.theme.ThemeVars.Text2)
                    .flexGrow(1)
            )
            // Figma 8217:321644 — 2×2 grid icon (21×21)
            Box(
                modifier = Modifier.sSize(24),
                contentAlignment = Alignment.Center
            ) {
                Icon(src = AppIcons.Dashboard, modifier = Modifier.sSize(21).styleModifier { property("filter", com.arena_web.ui.theme.ThemeVars.ICON_FILTER_V) })
            }
        }

        // Stories row (real stories + Add Story button for own profile).
        // `stories` is already sorted oldest → newest; we keep only the four
        // most recent so the row stays compact, but preserve the ascending
        // order inside that window — same list is handed to the viewer so the
        // popup's progress bar segments line up 1:1 with the circles shown.
        // When there are no stories at all we drop this row entirely — for
        // own profile the "+" creator moves inline next to Add post below;
        // for public profiles there's simply nothing to show.
        val hasStories = stories.isNotEmpty()
        val visibleStories = stories.takeLast(4)
        if (hasStories) {
            Box(modifier = Modifier.fillMaxWidth().sPadding(0, 24)) {
                StoriesRow(
                    isOwnProfile = isOwnProfile,
                    stories = visibleStories,
                    avatarUrl = userAvatarUrl,
                    onAddStoryClick = onAddStoryClick,
                    onStoryClick = { idx -> onOpenStory(visibleStories, idx) },
                )
            }
        }

        // Toggle + Add post row
        Row(
            modifier = Modifier.fillMaxWidth().sPadding(0, 24),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Row(modifier = Modifier.sGap(12).flexGrow(1), verticalAlignment = Alignment.CenterVertically) {
                // Figma 7917:401725 — Apps icon, visible ~16.5px inside 44×44
                ViewToggleButton(
                    icon = AppIcons.Apps,
                    iconSize = 16,
                    active = viewMode == ContentViewMode.Grid,
                    onClick = { viewMode = ContentViewMode.Grid }
                )
                // Figma 7917:401727 — Menu icon (3-line hamburger), visible 18×12 inside 44×44
                ViewToggleButton(
                    icon = AppIcons.Menu,
                    iconSize = 18,
                    active = viewMode == ContentViewMode.List,
                    onClick = { viewMode = ContentViewMode.List }
                )
            }
            Row(
                modifier = Modifier.sGap(8),
                verticalAlignment = Alignment.CenterVertically
            ) {
                // When the stories row is hidden, surface the Add Story creator
                // inline next to Add post — same 44×44 round footprint as the
                // pill's left cap, kept in the secondary-gradient style so it
                // reads as the same control just relocated.
                if (!hasStories && isOwnProfile) {
                    AddStoryPill(onClick = onAddStoryClick)
                }
                if (canCreatePost) {
                    AddPostPill(onClick = onAddPostClick)
                }
            }
        }

        // Posts container
        Box(
            modifier = Modifier.fillMaxWidth().flexGrow(1).minHeight(0.px).sPadding(0, 24)
        ) {
            if (effectivePosts.isEmpty()) {
                if (isLoadingPosts) {
                    Box(
                        modifier = Modifier.fillMaxWidth().sHeight(200),
                        contentAlignment = Alignment.Center
                    ) {
                        SpanText(
                            t.profileHubLoading,
                            modifier = Modifier.bodySRegular().color(com.arena_web.ui.theme.ThemeVars.Text3)
                        )
                    }
                } else {
                    NoPostsEmptyState(isOwnProfile = isOwnProfile)
                }
            } else {
                when (viewMode) {
                    ContentViewMode.Grid -> PostsGrid(
                        posts = effectivePosts,
                        onPostClick = onOpenComments,
                        biggerTiles = !hasStories,
                    )
                    ContentViewMode.List -> PostsList(
                        effectivePosts, userName, userAvatarUrl, activeCard, sportName,
                        sportProfile, onLikeClick,
                        onCommentClick = onOpenComments,
                        // Own profile only: "⋮" in the card header → Delete.
                        onDeleteClick = container.userPostRepository?.takeIf { isOwnProfile }?.let { repo ->
                            { post ->
                                onPostsUpdated(fetchedPosts.filter { it.id != post.id })
                                scope.launch { repo.deletePost(post.id) }
                            }
                        },
                    )
                }
            }
        }
    }
}

private enum class ContentViewMode { Grid, List }

@Composable
private fun StoriesRow(
    isOwnProfile: Boolean,
    stories: List<UserStoryDto>,
    avatarUrl: String?,
    onAddStoryClick: () -> Unit,
    onStoryClick: (Int) -> Unit = {},
) {
    // Figma 7991:176320 "Stories" — row height 110, Add button 80×80, stories 110×110
    Row(
        modifier = Modifier.fillMaxWidth().sHeight(110).sGap(8),
        verticalAlignment = Alignment.CenterVertically
    ) {
        // Figma 8217:321650 — Add Story Button: 80×80, secondary gradient bg,
        // ButtonBorderGradientPrimary as mask-composite overlay (same trick as the
        // chat "+" button — keeps the underlying bg untouched).
        if (isOwnProfile) {
            Box(
                modifier = Modifier
                    .sSize(80)
                    .sBorderRadius(100)
                    .cursor(Cursor.Pointer)
                    .onClick { onAddStoryClick() }
                    .styleModifier {
                        property("background", AppGradients.InteractiveSecondary)
                        property("position", "relative")
                    },
                contentAlignment = Alignment.Center
            ) {
                // Gradient border overlay (mask-composite)
                Box(
                    modifier = Modifier.fillMaxSize().styleModifier {
                        property("position", "absolute")
                        property("inset", "0")
                        property("border", "0.3px solid transparent")
                        property("border-radius", s(100))
                        property("background", "${AppGradients.ButtonBorderGradientPrimary} border-box")
                        property("-webkit-mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                        property("mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                        property("-webkit-mask-composite", "xor")
                        property("mask-composite", "exclude")
                        property("pointer-events", "none")
                    }
                )
                Icon(src = AppIcons.AddSecondary, modifier = Modifier.sSize(24).styleModifier { property("filter", com.arena_web.ui.theme.ThemeVars.ICON_FILTER_V) })
            }
        }
        // Caller already trims & sorts (oldest → newest within the visible
        // window), so we just render whatever was passed in.
        stories.forEachIndexed { idx, story ->
            StoryCircle(
                imageUrl = story.imageUrl,
                onClick = { onStoryClick(idx) },
            )
        }
    }
}

@Composable
private fun StoryCircle(imageUrl: String, onClick: () -> Unit = {}) {
    // Figma "Story Image" — 110×110, rounded full, with a thin gradient ring
    Box(
        modifier = Modifier
            .sSize(110)
            .sBorderRadius(100)
            .cursor(Cursor.Pointer)
            .onClick { onClick() }
            .styleModifier {
                property("background", "linear-gradient(135deg, rgba(0, 97, 255, 0.6), rgba(0, 97, 255, 0.15))")
                property("padding", s(3))
            },
        contentAlignment = Alignment.Center
    ) {
        Box(
            modifier = Modifier
                .fillMaxSize()
                .sBorderRadius(100)
                .styleModifier {
                    property("background-image", "url('$imageUrl')")
                    property("background-size", "cover")
                    property("background-position", "center")
                }
        )
    }
}

@Composable
private fun ViewToggleButton(
    icon: String,
    active: Boolean,
    onClick: () -> Unit,
    iconSize: Number = 20
) {
    val sportColors = LocalSportColors.current
    Box(
        modifier = Modifier
            .sSize(44)
            .sBorderRadius(100)
            .cursor(Cursor.Pointer)
            .onClick { onClick() }
            .then(
                if (active) {
                    // Figma 8217:322211 — solid sport-primary, no border
                    Modifier.backgroundColor(sportColors.primary)
                } else {
                    // Figma 8217:322209 — secondary gradient bg, bordered via overlay below
                    Modifier.styleModifier {
                        property("background", AppGradients.InteractiveSecondary)
                        property("position", "relative")
                    }
                }
            ),
        contentAlignment = Alignment.Center
    ) {
        // Gradient border overlay for inactive state (chat-style mask-composite)
        if (!active) {
            Box(
                modifier = Modifier.fillMaxSize().styleModifier {
                    property("position", "absolute")
                    property("inset", "0")
                    property("border", "0.3px solid transparent")
                    property("border-radius", s(100))
                    property("background", "${AppGradients.ButtonBorderGradientPrimary} border-box")
                    property("-webkit-mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                    property("mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                    property("-webkit-mask-composite", "xor")
                    property("mask-composite", "exclude")
                    property("pointer-events", "none")
                }
            )
        }
        Icon(
            src = icon,
            modifier = Modifier.sSize(iconSize).styleModifier {
                property(
                    "filter",
                    if (active) sportColors.iconFilterOnPrimary else com.arena_web.ui.theme.ThemeVars.ICON_FILTER_V
                )
            }
        )
    }
}

@Composable
private fun AddStoryPill(onClick: () -> Unit = {}) {
    val t = LocalAppStrings.current
    // Same elongated-pill silhouette as AddPostPill — circle (radius-44 left
    // cap) merging into a rounded label cap — but rendered as ONE shape so
    // the translucent AppGradients.InteractiveSecondary fill stays uniform and the gradient border
    // tracks a single continuous outline. Three overlapping boxes (as in
    // AddPostPill) would double-up the translucent fill at the seams and
    // break the border into visible vertical fragments.
    Row(
        modifier = Modifier
            .sHeight(44)
            .sBorderRadius(100)
            .cursor(Cursor.Pointer)
            .onClick { onClick() }
            .styleModifier {
                property("background", AppGradients.InteractiveSecondary)
                property("position", "relative")
                property("padding", "0 ${s(24)} 0 ${s(12)}")
            },
        verticalAlignment = Alignment.CenterVertically
    ) {
        Box(
            modifier = Modifier.fillMaxSize().styleModifier {
                property("position", "absolute")
                property("inset", "0")
                property("border", "0.3px solid transparent")
                property("border-radius", s(100))
                property("background", "${AppGradients.ButtonBorderGradientPrimary} border-box")
                property("-webkit-mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                property("mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                property("-webkit-mask-composite", "xor")
                property("mask-composite", "exclude")
                property("pointer-events", "none")
            }
        )
        Icon(src = AppIcons.AddSecondary, modifier = Modifier.sSize(16).styleModifier { property("filter", com.arena_web.ui.theme.ThemeVars.ICON_FILTER_V) })
        Box(modifier = Modifier.sWidth(11))
        SpanText(
            t.profileHubAddStory,
            modifier = Modifier
                .bodyLMedium()
                .color(com.arena_web.ui.theme.ThemeVars.Text2)
                .styleModifier { property("white-space", "nowrap") }
        )
    }
}

@Composable
private fun AddPostPill(onClick: () -> Unit = {}) {
    val t = LocalAppStrings.current
    val sportColors = LocalSportColors.current
    Row(
        modifier = Modifier
            .cursor(Cursor.Pointer)
            .position(Position.Relative)
            .onClick { onClick() },
        verticalAlignment = Alignment.CenterVertically
    ) {
        Box(
            modifier = Modifier
                .sSize(44)
                .sBorderRadius(100)
                .backgroundColor(sportColors.primary)
                .overflow(Overflow.Hidden)
                .zIndex(1),
            contentAlignment = Alignment.Center
        ) {
            Icon(
                src = AppIcons.Add,
                modifier = Modifier.styleModifier {
                    property("filter", sportColors.iconFilterOnPrimary)
                }
            )
        }
        Box(
            modifier = Modifier
                .sWidth(7)
                .sHeight(16)
                .backgroundColor(sportColors.primary)
                .sMarginLeft(-4)
                .zIndex(0)
        )
        Box(
            modifier = Modifier
                .sHeight(44)
                .sBorderRadius(22)
                .backgroundColor(sportColors.primary)
                .sPadding(10, 24)
                .sMarginLeft(-4),
            contentAlignment = Alignment.Center
        ) {
            SpanText(
                t.profileHubAddPost,
                modifier = Modifier
                    .bodyLMedium()
                    .color(sportColors.onPrimary)
                    .styleModifier { property("white-space", "nowrap") }
            )
        }
    }
}

@Composable
private fun PostsGrid(
    posts: List<UserPostDto>,
    onPostClick: (UserPostDto) -> Unit = {},
    biggerTiles: Boolean = false,
) {
    // Figma 7917:401735 — fixed 151×151 tiles, 6px gap, 7.79 radius, centered.
    // Fixed tile size (not 1fr) keeps cells small enough that ≥2 rows fit in
    // the visible Player Hub viewport without overflowing. When the stories
    // row is hidden we switch to 1fr columns so tiles fluidly fill the freed
    // horizontal space (minus the parent's 24px h-padding and 6px gaps) and
    // grow in lockstep via aspect-ratio: 1/1 — that way the section's outer
    // padding is always respected regardless of viewport width.
    Div(attrs = {
        style {
            width(100.percent)
            display(DisplayStyle.Grid)
            if (biggerTiles) {
                property("grid-template-columns", "repeat(3, 1fr)")
            } else {
                property("grid-template-columns", "repeat(3, ${s(151)})")
            }
            property("gap", s(6))
            property("justify-content", "center")
        }
    }) {
        // Iterate posts (not just images) so each tile can carry its parent
        // post into the click handler — clicking opens the comments popup
        // for that specific post.
        posts.filter { it.images.isNotEmpty() }.take(9).forEach { post ->
            val img = post.images.first()
            val hasMultipleImages = post.images.size > 1
            Div(attrs = {
                style {
                    property("position", "relative")
                    if (biggerTiles) {
                        property("width", "100%")
                        property("aspect-ratio", "1 / 1")
                    } else {
                        property("width", s(151))
                        property("height", s(151))
                    }
                    property("border-radius", s(7.79))
                    property("overflow", "hidden")
                    property("background-image", "url('${img.imageUrl}')")
                    property("background-size", "cover")
                    property("background-position", "center")
                    property("cursor", "pointer")
                }
                onClick { onPostClick(post) }
            }) {
                // Figma 9300:142883 — overlay the "multi-photo" glyph at the
                // top-right corner (24×24, 6px inset) when the post carries
                // more than one image. Tile click still routes through the
                // parent Div via pointer-events: none.
                if (hasMultipleImages) {
                    Img(
                        src = AppIcons.PostMulti,
                        alt = "",
                        attrs = {
                            style {
                                property("position", "absolute")
                                property("top", s(6))
                                property("right", s(6))
                                property("width", s(24))
                                property("height", s(24))
                                property("pointer-events", "none")
                            }
                        }
                    )
                }
            }
        }
    }
}

@Composable
private fun PostsList(
    posts: List<UserPostDto>,
    userName: String,
    userAvatarUrl: String?,
    activeCard: UserCardDto?,
    sportName: String,
    sportProfile: SportProfileDto?,
    onLikeClick: (UserPostDto) -> Unit = {},
    onCommentClick: (UserPostDto) -> Unit = {},
    onDeleteClick: ((UserPostDto) -> Unit)? = null,
) {
    // Trailing bottom padding so the last post has breathing room above the
    // scroll container's edge instead of butting up against it.
    Column(modifier = Modifier.fillMaxWidth().sGap(40).sPaddingBottom(40)) {
        posts.forEach { post ->
            PostListCard(
                post, userName, userAvatarUrl, activeCard, sportName, sportProfile,
                onLikeClick, onCommentClick,
                onDelete = onDeleteClick?.let { { it(post) } },
            )
        }
    }
}

@Composable
private fun PostListCard(
    post: UserPostDto,
    userName: String,
    userAvatarUrl: String?,
    activeCard: UserCardDto?,
    sportName: String,
    sportProfile: SportProfileDto?,
    onLikeClick: (UserPostDto) -> Unit = {},
    onCommentClick: (UserPostDto) -> Unit = {},
    onDelete: (() -> Unit)? = null,
) {
    val t = LocalAppStrings.current
    // Avatar comes ONLY from the active card's AI-generated slot — the Google
    // profile avatar (userAvatarUrl) is intentionally NOT used. Fall back to our
    // empty-avatar placeholder if the card has no avatar yet.
    val resolvedAvatar = activeCard?.avatarUrl ?: AppIcons.EmptyAvatar
    Column(modifier = Modifier.fillMaxWidth().sGap(14)) {
        // Header: player card + name/date/city stack
        Row(
            modifier = Modifier.fillMaxWidth().sGap(12),
            verticalAlignment = Alignment.CenterVertically
        ) {
            if (activeCard != null) {
                // Figma 8636:428411 — small 40×60 player card. Highlight size (37×58)
                // matches the design footprint. Rating overlay & player name are
                // hidden because their typography (22 / 12 px) was sized for larger
                // cards and overflows the Highlight frame; the silhouette + avatar
                // still convey the card identity.
                PlayerCard(
                    playerName = "",
                    rating = 0,
                    position = "",
                    sport = sportName,
                    size = PlayerCardSize.Highlight,
                    cardAssetsBasePath = activeCard.assetsBasePath,
                    cardTextColor = activeCard.textColor,
                    cardFrameUrl = activeCard.cardImageUrl,
                    avatarUrl = resolvedAvatar,
                    hideRating = true,
                    hideShadow = true,
                )
            } else {
                Box(
                    modifier = Modifier
                        .sSize(44)
                        .sBorderRadius(100)
                        .backgroundColor(AppColors.Dark.SurfaceVariant)
                        .overflow(Overflow.Hidden)
                        .styleModifier {
                            if (!userAvatarUrl.isNullOrBlank()) {
                                property("background-image", "url('$userAvatarUrl')")
                                property("background-size", "cover")
                                property("background-position", "center")
                            }
                        }
                )
            }
            Column(modifier = Modifier.flexGrow(1).sGap(6)) {
                Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.sGap(8)) {
                    SpanText(
                        userName.ifBlank { t.profileHubUserFallback },
                        modifier = Modifier
                            .sFontSize(16)
                            .fontWeight(AppTypography.Medium)
                            .sLineHeight(18)
                            .color(com.arena_web.ui.theme.ThemeVars.Text1)
                    )
                    // Bullet separator — 3px dot per Figma
                    Box(
                        modifier = Modifier
                            .sSize(3)
                            .sBorderRadius(100)
                            .backgroundColor(AppColors.Text.Muted)
                    )
                    SpanText(
                        formatPostDate(post.createdAt),
                        modifier = Modifier
                            .sFontSize(16)
                            .fontWeight(AppTypography.Regular)
                            .sLineHeight(24)
                            .sLetterSpacing(0.16)
                            .color(com.arena_web.ui.theme.ThemeVars.Text3)
                    )
                }
                if (!post.city.isNullOrBlank()) {
                    SpanText(
                        post.city!!,
                        modifier = Modifier
                            .sFontSize(14)
                            .fontWeight(AppTypography.Light)
                            .sLineHeight(18)
                            .sLetterSpacing(0.028)
                            .color(com.arena_web.ui.theme.ThemeVars.Text3)
                    )
                }
            }
            if (onDelete != null) {
                com.arena_web.ui.components.widgets.KebabDeleteMenu(onDelete = onDelete)
            }
        }
        // Body text
        if (post.content.isNotBlank()) {
            SpanText(
                post.content,
                modifier = Modifier
                    .fillMaxWidth()
                    .sFontSize(16)
                    .fontWeight(AppTypography.Regular)
                    .sLineHeight(24)
                    .sLetterSpacing(0.16)
                    .color(com.arena_web.ui.theme.ThemeVars.Text2)
            )
        }
        // Images
        if (post.images.isNotEmpty()) {
            PostImageCollage(post.images.map { it.imageUrl }, post.layout)
        }
        // Figma 8702:137173 — Like + Comment row: heart icon + count, then chat
        // icon + count. 18px gap between groups, 8px between icon and count.
        PostLikeCommentRow(
            likesCount = post.likesCount,
            commentsCount = post.commentsCount,
            isLiked = post.isLikedByMe,
            onLikeClick = { onLikeClick(post) },
            onCommentClick = { onCommentClick(post) },
        )
    }
}

@Composable
private fun PostLikeCommentRow(
    likesCount: Int,
    commentsCount: Int,
    isLiked: Boolean,
    onLikeClick: () -> Unit,
    onCommentClick: () -> Unit = {},
) {
    Row(
        modifier = Modifier.sGap(18),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Row(
            modifier = Modifier.sGap(8).cursor(Cursor.Pointer).onClick { onLikeClick() },
            verticalAlignment = Alignment.CenterVertically
        ) {
            com.varabyte.kobweb.silk.components.graphics.Image(
                src = AppIcons.Heart,
                modifier = Modifier.sSize(24).styleModifier {
                    if (isLiked) {
                        // Tint to red (#FF3B30 family) when liked, matching the
                        // filled-heart style used in FeedPostCard.
                        property(
                            "filter",
                            "brightness(0) saturate(100%) invert(27%) sepia(95%) saturate(4997%) hue-rotate(344deg) brightness(95%) contrast(99%)"
                        )
                    }
                }
            )
            SpanText(
                "$likesCount",
                modifier = Modifier
                    .sFontSize(18)
                    .fontWeight(AppTypography.Regular)
                    .sLineHeight(18)
                    .sLetterSpacing(0.5)
                    .color(com.arena_web.ui.theme.ThemeVars.Text2)
            )
        }
        Row(
            modifier = Modifier.sGap(8).cursor(Cursor.Pointer).onClick { onCommentClick() },
            verticalAlignment = Alignment.CenterVertically
        ) {
            Icon(
                src = AppIcons.Chat,
                modifier = Modifier.sSize(21)
            )
            SpanText(
                "$commentsCount",
                modifier = Modifier
                    .sFontSize(18)
                    .fontWeight(AppTypography.Regular)
                    .sLineHeight(18)
                    .sLetterSpacing(0.5)
                    .color(com.arena_web.ui.theme.ThemeVars.Text2)
            )
        }
    }
}

// Shared with the mobile Player Hub (ProfileContentMobile.kt PostListCard).
internal fun formatPostDate(iso: String): String = try {
    val d = kotlin.js.Date(iso)
    val months = listOf("Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec")
    "${d.getDate()} ${months[d.getMonth().toInt()]}"
} catch (_: Throwable) { iso }

@Composable
private fun EmptyTabState(text: String) {
    Box(
        modifier = Modifier.fillMaxSize().sPadding(32),
        contentAlignment = Alignment.Center
    ) {
        SpanText(
            text,
            modifier = Modifier.bodySRegular().color(com.arena_web.ui.theme.ThemeVars.Text3)
        )
    }
}

// Figma 9305:145332 — empty state for the Content tab's posts area.
@Composable
private fun NoPostsEmptyState(isOwnProfile: Boolean) {
    val t = LocalAppStrings.current
    Column(
        modifier = Modifier.fillMaxWidth().sPaddingTop(60).sGap(28),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        // 144×126 in the icon catalog → scale uniformly: use width=72 and let
        // height ride aspect; TintedGlyph is square, so we pick the longer
        // dimension and trust the SVG to centre itself.
        TintedGlyph(src = AppIcons.EmptyImagesStack, sizePx = 96)
        Column(
            modifier = Modifier.sGap(12),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            SpanText(
                t.profileHubNoPostsYet,
                modifier = Modifier.h2().color(com.arena_web.ui.theme.ThemeVars.Text2)
            )
            SpanText(
                if (isOwnProfile) t.profileHubShareFirstMoment else t.profileHubPlayerNotPosted,
                modifier = Modifier.bodyXlMedium().color(com.arena_web.ui.theme.ThemeVars.Text2)
            )
        }
    }
}








